import React, { useState, useRef } from "react";
import {
  Search, Plus, Filter, ChevronDown, ChevronUp,
  Layers, Table, Edit3, Trash2, FileText, Upload, X, Image
} from "lucide-react";

// ── Static table data ─────────────────────────────────────────────────────────
const INITIAL_DATA = [
  {
    id: 1,
    title: "Smooth & Dark",
    status: "Active",
    sections: [
      {
        image: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/74/Pencil_drawing.jpg/320px-Pencil_drawing.jpg",
        description: "Experience unparalleled writing with the high-quality dark graphite lead of our pencil, optimized for perfect pressure and precision.",
      },
      {
        image: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/74/Pencil_drawing.jpg/320px-Pencil_drawing.jpg",
        description: "Perfect for artists and professionals who demand consistency.",
      },
    ],
  },
  {
    id: 2,
    title: "Premium Eraser",
    status: "Active",
    sections: [
      {
        image: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/74/Pencil_drawing.jpg/320px-Pencil_drawing.jpg",
        description: "Clean and precise erasing with the premium eraser attached to every pencil, leaving no smudges behind.",
      },
    ],
  },
  {
    id: 3,
    title: "Name Engraving",
    status: "Inactive",
    sections: [
      {
        image: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/74/Pencil_drawing.jpg/320px-Pencil_drawing.jpg",
        description: "Personalize your pencil with a custom name engraving for a unique and memorable gifting experience.",
      },
    ],
  },
];

/* ─── Toggle Switch ─── */
const ToggleSwitch = ({ isActive, onToggle }) => (
  <button
    onClick={onToggle}
    className={`relative inline-flex items-center w-9 h-5 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 ${
      isActive ? "bg-emerald-500 focus:ring-emerald-400" : "bg-gray-300 focus:ring-gray-400"
    }`}
  >
    <span
      className={`inline-block w-3.5 h-3.5 bg-white rounded-full shadow transform transition-transform duration-200 ${
        isActive ? "translate-x-4" : "translate-x-0.5"
      }`}
    />
  </button>
);

/* ─── Delete Confirmation Modal ─── */
const DeleteModal = ({ item, onCancel, onConfirm }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
    <div className="relative bg-white rounded-xl shadow-lg w-[90%] max-w-md p-6 z-10">
      <h2 className="text-lg font-semibold mb-2">Delete Product Detail</h2>
      <p className="text-gray-600 text-sm mb-6">
        Are you sure you want to delete{" "}
        <span className="font-semibold text-gray-800">{item?.title}</span>? This action cannot be undone.
      </p>
      <div className="flex justify-end gap-3">
        <button onClick={onCancel} className="px-4 py-2 text-sm rounded-lg border hover:bg-gray-100">
          Cancel
        </button>
        <button onClick={onConfirm} className="px-4 py-2 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600">
          Delete
        </button>
      </div>
    </div>
  </div>
);

/* ─── Image-Description Section ─── */
const EMPTY_SECTION = () => ({ image: "", description: "" });

const ImageDescriptionSection = ({ secIdx, section, onChange, onRemove, canRemove }) => {
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onChange(secIdx, "image", ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onChange(secIdx, "image", ev.target.result);
    reader.readAsDataURL(file);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3 relative">
      {/* Section header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center">
            {secIdx + 1}
          </span>
          <span className="text-xs font-semibold text-gray-600">Image & Description</span>
        </div>
        {canRemove && (
          <button
            onClick={() => onRemove(secIdx)}
            className="flex items-center gap-1 text-xs text-red-400 hover:text-red-600 hover:bg-red-50 px-2 py-1 rounded-lg border border-red-100 transition-colors"
          >
            <X size={11} /> Remove
          </button>
        )}
      </div>

      {/* Image Upload */}
      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png, image/jpeg, image/webp"
          className="hidden"
          onChange={handleFileChange}
        />
        {section.image ? (
          <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-white h-28">
            <img src={section.image} alt="preview" className="w-full h-full object-contain" />
            <button
              onClick={() => onChange(secIdx, "image", "")}
              className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
            >
              <X size={12} />
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="absolute bottom-2 right-2 text-xs bg-white border border-gray-200 text-gray-600 px-2 py-1 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Change
            </button>
          </div>
        ) : (
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            className="border-2 border-dashed border-gray-200 rounded-xl h-20 flex flex-col items-center justify-center gap-1 bg-amber-50/30 cursor-pointer hover:border-amber-400 hover:bg-amber-50/60 transition-colors"
          >
            <Upload size={16} className="text-gray-400" />
            <p className="text-xs text-gray-500">
              <span className="font-semibold text-gray-600">Click to upload</span> or drag and drop
            </p>
            <p className="text-xs text-gray-400">PNG, JPG, WebP (MAX. 5MB)</p>
          </div>
        )}
      </div>

      {/* Description */}
      <div>
        <textarea
          value={section.description}
          onChange={(e) => onChange(secIdx, "description", e.target.value.slice(0, 200))}
          placeholder="Enter description for this image..."
          rows={2}
          className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-transparent transition resize-none"
        />
        <div className="flex justify-between mt-0.5">
          <span className="text-xs text-gray-400">Describe what this image highlights</span>
          <span className="text-xs text-gray-400">{section.description.length}/200</span>
        </div>
      </div>
    </div>
  );
};

/* ─── Detail Card (inside modal) ─── */
const DetailCard = ({ index, entry, onChange, onRemove, canRemove }) => {
  const MAX_SECTIONS = 6;

  const handleSectionChange = (secIdx, field, value) => {
    const updated = entry.sections.map((s, i) => i === secIdx ? { ...s, [field]: value } : s);
    onChange(index, "sections", updated);
  };

  const handleAddSection = () => {
    if (entry.sections.length < MAX_SECTIONS) {
      onChange(index, "sections", [...entry.sections, EMPTY_SECTION()]);
    }
  };

  const handleRemoveSection = (secIdx) => {
    onChange(index, "sections", entry.sections.filter((_, i) => i !== secIdx));
  };

  return (
    <div className="bg-amber-50/60 rounded-2xl p-5 space-y-5 relative">
      {/* Card header */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold text-teal-800">Detail {index + 1}</span>
        {canRemove && (
          <button
            onClick={() => onRemove(index)}
            className="flex items-center gap-1.5 text-xs font-medium text-red-500 border border-red-200 hover:bg-red-50 rounded-lg px-3 py-1.5 transition-colors"
          >
            <Trash2 size={13} /> Remove
          </button>
        )}
      </div>

      {/* Title */}
      <div>
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-2">
          <span className="w-2 h-2 rounded-full bg-orange-400 inline-block" />
          Title <span className="text-orange-500">*</span>
        </label>
        <input
          type="text"
          value={entry.title}
          maxLength={50}
          onChange={(e) => onChange(index, "title", e.target.value)}
          placeholder="Enter product detail title..."
          className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-transparent transition"
        />
        <div className="flex justify-between mt-1">
          <span className="text-xs text-gray-400">Make it specific and descriptive</span>
          <span className="text-xs text-gray-400">{entry.title.length}/50</span>
        </div>
      </div>

      {/* Image-Description Sections */}
      <div>
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-3">
          <span className="w-2 h-2 rounded-full bg-orange-400 inline-block" />
          Images & Descriptions <span className="text-orange-500">*</span>
          <span className="ml-auto text-xs font-normal text-gray-400">
            {entry.sections.length}/{MAX_SECTIONS}
          </span>
        </label>

        <div className="space-y-3">
          {entry.sections.map((section, secIdx) => (
            <ImageDescriptionSection
              key={secIdx}
              secIdx={secIdx}
              section={section}
              onChange={handleSectionChange}
              onRemove={handleRemoveSection}
              canRemove={entry.sections.length > 1}
            />
          ))}
        </div>

        {entry.sections.length < MAX_SECTIONS && (
          <button
            onClick={handleAddSection}
            className="mt-3 w-full flex items-center justify-center gap-2 border border-dashed border-amber-300 text-amber-600 hover:bg-amber-50 rounded-xl py-2.5 text-xs font-semibold transition-colors"
          >
            <Plus size={14} /> Add Image & Description
          </button>
        )}
      </div>

      {/* Status */}
      <div>
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-2">
          <span className="w-2 h-2 rounded-full bg-orange-400 inline-block" />
          Status <span className="text-orange-500">*</span>
        </label>
        <div className="flex items-center gap-3 flex-wrap">
          {["Active", "Inactive"].map((s) => (
            <button
              key={s}
              onClick={() => onChange(index, "status", s)}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 transition-all text-sm font-medium ${
                entry.status === s
                  ? s === "Active"
                    ? "border-green-400 bg-green-50 text-green-700"
                    : "border-gray-400 bg-gray-100 text-gray-700"
                  : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
              }`}
            >
              <span className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                entry.status === s
                  ? s === "Active" ? "border-green-500 bg-green-500" : "border-gray-500 bg-gray-500"
                  : "border-gray-300"
              }`}>
                {entry.status === s && <span className="w-1 h-1 rounded-full bg-white" />}
              </span>
              {s}
              <span className={`text-xs font-normal ${s === "Active" ? "text-green-600" : "text-gray-400"}`}>
                {s === "Active" ? "Visible" : "Hidden"}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─── Add / Edit Modal ─── */
const EMPTY_ENTRY = () => ({
  title: "",
  status: "Active",
  sections: [EMPTY_SECTION()],
});

const DetailModal = ({ isOpen, onClose, onSave, editingItem }) => {
  const [entries, setEntries] = useState(
    editingItem
      ? [{ title: editingItem.title, status: editingItem.status, sections: editingItem.sections || [EMPTY_SECTION()] }]
      : [EMPTY_ENTRY()]
  );

  if (!isOpen) return null;

  const isEdit = !!editingItem;
  const MAX_ENTRIES = 6;

  const handleChange = (idx, field, value) => {
    setEntries((prev) => prev.map((e, i) => i === idx ? { ...e, [field]: value } : e));
  };

  const handleAddMore = () => {
    if (entries.length < MAX_ENTRIES) setEntries((prev) => [...prev, EMPTY_ENTRY()]);
  };

  const handleRemove = (idx) => {
    setEntries((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    const valid = entries.filter((e) => e.title.trim());
    if (!valid.length) return;
    if (isEdit) {
      onSave(valid[0]);
    } else {
      onSave(valid);
    }
  };

  return (
    <div className="min-h-screen fixed inset-0 z-50 flex items-end justify-end">
      <style>{`
        @keyframes modalPop {
          from { transform: translateY(20px) scale(0.97); opacity: 0; }
          to   { transform: translateY(0) scale(1); opacity: 1; }
        }
      `}</style>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative w-full max-w-[560px] max-h-[100vh] bg-gray-50 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ animation: "modalPop 0.25s cubic-bezier(.4,0,.2,1)" }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 left-4 z-10 w-8 h-8 flex items-center justify-center bg-gray-900 text-white rounded-full hover:bg-gray-700 transition-colors"
        >
          <X size={15} />
        </button>

        <div className="pt-10 pb-4 px-8 text-center shrink-0">
          <h2 className="text-2xl font-extrabold text-teal-800 tracking-tight">
            {isEdit ? "Edit Product Detail" : "Add New Product Detail"}
          </h2>
          <div className="mx-auto mt-2 w-16 h-0.5 bg-amber-400 rounded-full" />
          {!isEdit && (
            <p className="text-xs text-gray-500 mt-2">
              {entries.length} of {MAX_ENTRIES} details added
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-4 space-y-4">
          {entries.map((entry, idx) => (
            <DetailCard
              key={idx}
              index={idx}
              entry={entry}
              onChange={handleChange}
              onRemove={handleRemove}
              canRemove={entries.length > 1}
            />
          ))}
          {!isEdit && entries.length < MAX_ENTRIES && (
            <button
              onClick={handleAddMore}
              className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-amber-400 text-amber-600 hover:bg-amber-50 rounded-2xl py-3.5 text-sm font-semibold transition-colors"
            >
              <Plus size={16} /> Add More
            </button>
          )}
        </div>

        <div className="px-8 py-4 border-t border-gray-100 flex gap-3 bg-gray-50 shrink-0">
          <button
            onClick={onClose}
            className="flex-1 py-3 text-sm font-semibold rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex-1 py-3 text-sm font-bold rounded-xl bg-teal-700 text-white hover:bg-teal-600 transition-colors"
          >
            {isEdit ? "Update Detail" : `Save ${entries.length > 1 ? `All (${entries.length})` : "Detail"}`}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Main Page ─── */
export default function Productdetails() {
  const [data, setData] = useState(INITIAL_DATA);
  const [search, setSearch] = useState("");
  const [viewType, setViewType] = useState("list");
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortBy, setSortBy] = useState("A-Z");
  const [isModalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const openAdd = () => { setEditingItem(null); setModalOpen(true); };
  const openEdit = (item) => { setEditingItem(item); setModalOpen(true); };

  const handleSave = (formData) => {
    if (editingItem) {
      setData((prev) => prev.map((d) => d.id === editingItem.id ? { ...d, ...formData } : d));
    } else {
      const newItems = (Array.isArray(formData) ? formData : [formData]).map((item) => ({
        id: Date.now() + Math.random(),
        sections: [],
        ...item,
      }));
      setData((prev) => [...prev, ...newItems]);
    }
    setModalOpen(false);
  };

  const handleDeleteClick = (item) => { setSelectedItem(item); setShowDeleteModal(true); };
  const confirmDelete = () => {
    setData((prev) => prev.filter((d) => d.id !== selectedItem.id));
    setShowDeleteModal(false);
  };

  const toggleStatus = (id) => {
    setData((prev) =>
      prev.map((d) => d.id === id ? { ...d, status: d.status === "Active" ? "Inactive" : "Active" } : d)
    );
  };

  let filtered = data.filter((d) =>
    (d.title || "").toLowerCase().includes(search.toLowerCase())
  );
  if (statusFilter !== "All") filtered = filtered.filter((d) => d.status === statusFilter);
  if (sortBy === "A-Z") filtered.sort((a, b) => a.title.localeCompare(b.title));
  if (sortBy === "Z-A") filtered.sort((a, b) => b.title.localeCompare(a.title));

  // Helper: first image from sections
  const getFirstImage = (item) => item.sections?.[0]?.image || "";
  const getFirstDesc = (item) => item.sections?.[0]?.description || "";

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="bg-teal-700 text-white p-2 rounded-lg">
            <FileText size={28} />
          </div>
          <div>
            <h1 className="text-xl md:text-3xl font-bold text-teal-800">Product Details</h1>
            <p className="text-gray-600 text-xs md:text-sm">
              Manage display sections shown on the product page
            </p>
          </div>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-1 md:gap-2 bg-teal-700 text-white px-3 md:px-4 py-2.5 md:py-4 text-sm md:text-base rounded-lg hover:bg-teal-600"
        >
          <Plus size={16} />
          <span className="hidden sm:inline">Add Product Detail</span>
        </button>
      </div>

      {/* Search + Filter Row */}
      <div className="bg-white p-4 rounded-xl shadow-sm mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-3 border rounded-lg px-3 py-2 w-full md:w-1/2">
            <Search className="text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search product details..."
              className="w-full outline-none text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 border border-teal-600 text-teal-700 px-4 py-2 rounded-lg hover:bg-teal-50 w-fit"
          >
            <Filter size={16} />
            Filters
            {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
        {showFilters && (
          <div className="mt-4 border rounded-xl p-4 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Status</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full border rounded-lg px-3 py-2">
                <option value="All">All Details</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Sort By</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="w-full border rounded-lg px-3 py-2">
                <option value="A-Z">Title A-Z</option>
                <option value="Z-A">Title Z-A</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Count + View Toggle */}
      <div className="flex items-center justify-between mb-6">
        <span className="text-sm text-gray-600">
          Showing {filtered.length} of {data.length} Product Details
        </span>
        <div className="flex items-center gap-2">
          <button onClick={() => setViewType("list")} className={`p-2 border rounded-lg ${viewType === "list" ? "bg-teal-700 text-white" : "bg-white"}`}>
            <Layers size={18} />
          </button>
          <button onClick={() => setViewType("table")} className={`p-2 border rounded-lg ${viewType === "table" ? "bg-teal-700 text-white" : "bg-white"}`}>
            <Table size={18} />
          </button>
        </div>
      </div>

      {/* List View */}
      {viewType === "list" && (
        <div className="flex flex-col gap-4">
          {filtered.map((item) => (
            <div key={item.id} className="bg-white border rounded-xl p-4 flex items-center justify-between hover:shadow-md transition">
              <div className="flex items-center gap-4">
                {/* Image stack preview */}
                <div className="relative w-12 h-12 flex-shrink-0">
                  {(item.sections || []).slice(0, 3).map((sec, i) => (
                    <img
                      key={i}
                      src={sec.image || "https://via.placeholder.com/48"}
                      alt=""
                      className="w-11 h-11 object-contain rounded-lg border border-gray-200 bg-gray-50 absolute"
                      style={{ top: i * 2, left: i * 2, zIndex: 3 - i }}
                    />
                  ))}
                  {!item.sections?.length && (
                    <img src="https://via.placeholder.com/48" alt={item.title} className="w-11 h-11 object-contain rounded-lg border border-gray-200 bg-gray-50" />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-800">{item.title}</h3>
                    {item.sections?.length > 1 && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">
                        <Image size={10} className="inline mr-0.5" />{item.sections.length} images
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 max-w-xs line-clamp-1">{getFirstDesc(item)}</p>
                  <span className={`text-xs px-2 py-0.5 mt-1 rounded-full inline-block ${item.status === "Active" ? "bg-green-100 text-green-600" : "bg-gray-200 text-gray-600"}`}>
                    {item.status}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <ToggleSwitch isActive={item.status === "Active"} onToggle={() => toggleStatus(item.id)} />
                <button onClick={() => openEdit(item)} className="p-2 rounded-lg text-teal-600 hover:bg-teal-50 transition-all" title="Edit">
                  <Edit3 size={16} />
                </button>
                <button onClick={() => handleDeleteClick(item)} className="p-2 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-all" title="Delete">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Table View */}
      {viewType === "table" && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-teal-700 text-white">
              <tr>
                <th className="p-3 text-left">#</th>
                <th className="p-3 text-left">Image</th>
                <th className="p-3 text-left">Title</th>
                <th className="p-3 text-left hidden md:table-cell">Description</th>
                <th className="p-3 text-left">Images</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item, i) => (
                <tr key={item.id} className="border-t hover:bg-teal-50/30 transition-colors">
                  <td className="p-3 text-gray-500">{i + 1}</td>
                  <td className="p-3">
                    <img
                      src={getFirstImage(item) || "https://via.placeholder.com/48"}
                      alt={item.title}
                      className="w-11 h-11 object-contain rounded-lg border border-gray-200 bg-gray-50"
                    />
                  </td>
                  <td className="p-3 font-medium text-gray-800">{item.title}</td>
                  <td className="p-3 text-gray-500 hidden md:table-cell max-w-xs">
                    <span className="line-clamp-1">{getFirstDesc(item)}</span>
                  </td>
                  <td className="p-3">
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                      {item.sections?.length || 0} image{(item.sections?.length || 0) !== 1 ? "s" : ""}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${item.status === "Active" ? "bg-green-100 text-green-600" : "bg-gray-200 text-gray-600"}`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <ToggleSwitch isActive={item.status === "Active"} onToggle={() => toggleStatus(item.id)} />
                      <button onClick={() => openEdit(item)} className="p-2 rounded-lg text-teal-700 hover:bg-teal-50 transition-all" title="Edit">
                        <Edit3 size={15} />
                      </button>
                      <button onClick={() => handleDeleteClick(item)} className="p-2 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-all" title="Delete">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center text-center py-20">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <FileText className="text-gray-400" size={36} />
          </div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No product details found</h3>
          <p className="text-gray-500 text-sm mb-6">Try adjusting your search or add a new product detail.</p>
          <button onClick={openAdd} className="flex items-center gap-2 bg-teal-700 text-white px-5 py-2.5 rounded-lg hover:bg-teal-600">
            <Plus size={18} /> Add Product Detail
          </button>
        </div>
      )}

      <DetailModal isOpen={isModalOpen} onClose={() => setModalOpen(false)} onSave={handleSave} editingItem={editingItem} />
      {showDeleteModal && <DeleteModal item={selectedItem} onCancel={() => setShowDeleteModal(false)} onConfirm={confirmDelete} />}
    </div>
  );
}